import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link as RouterLink, Outlet } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Divider,
  List,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Avatar,
  Drawer,
  useMediaQuery,
  useTheme,
  ListItemButton,
  Button,
  Tooltip,
  Fab
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assessment,
  VpnKey,
  Description,
  Person,
  Menu as MenuIcon,
  Logout,
  ChevronLeft as ChevronLeftIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  ChevronRight as ChevronRightIcon,
  Lock as LockIcon,
  Biotech as BiotechIcon,
  SupportAgent as SupportAgentIcon,
  CloudUpload as CloudUploadIcon,
  Group as GroupIcon,
  MoveToInbox as InboxIcon
} from '@mui/icons-material';
import authService from '../services/authService';

// Define drawer width constant
const DRAWER_WIDTH = 240;
const COLLAPSED_DRAWER_WIDTH = 65;

// Define interface for navigation items
interface NavItem {
  text?: string;
  icon?: React.ReactNode;
  path?: string;
  divider?: boolean;
}

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  
  // Check if user is authenticated - if not, redirect to login
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      console.log("Layout: Not authenticated, redirecting to login");
      navigate('/login', { replace: true });
    } else {
      console.log("Layout: User is authenticated");
    }
  }, [navigate]);

  // Handle drawer state when screen size changes
  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);
  

  const handleLogout = () => {
    console.log("Logging out user");
    // Use authService to logout
    authService.logout();
    // Redirect to login
    navigate('/login', { replace: true });
  };
  
  const toggleDrawer = () => {
    if (isMobile) {
      setDrawerOpen(!drawerOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  // Check if the current route matches
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  // Navigation items for the sidebar
  const navigationItems: NavItem[] = [
    { text: 'Dashboard', icon: <HomeIcon />, path: '/dashboard' },
    { divider: true },
    { text: 'My Labs', icon: <BiotechIcon />, path: '/lis-portal/labs' },
    { text: 'Integration Setup', icon: <SettingsIcon />, path: '/lis-portal/integration-setup' },
    { text: 'Organism Mapping', icon: <BiotechIcon />, path: '/lis-portal/organism-mapping' },
    { text: 'File Transfer', icon: <CloudUploadIcon />, path: '/lis-portal/files' },
    { text: 'API Management', icon: <VpnKey />, path: '/lis-portal/api-keys' },
    { text: 'Webhook Secrets', icon: <LockIcon />, path: '/lis-portal/webhook-secrets' },
    { text: 'Usage Statistics', icon: <Assessment />, path: '/lis-portal/usage' },
    { text: 'Support Tickets', icon: <SupportAgentIcon />, path: '/lis-portal/support-tickets' },
    { text: 'Team', icon: <GroupIcon />, path: '/lis-portal/team' },
    { text: 'Access Requests', icon: <InboxIcon />, path: '/lis-portal/access-requests' },
    { divider: true },
    { text: 'Documentation', icon: <Description />, path: '/lis-portal/documentation' },
    { text: 'Integration Guide', icon: <Description />, path: '/lis-portal/integration-guide' },
    { text: 'Vendor API Guide', icon: <Description />, path: '/lis-portal/vendor-api-guide' },
    { text: 'API Examples', icon: <Description />, path: '/lis-portal/api-examples' },
    { divider: true },
    { text: 'Account', icon: <SettingsIcon />, path: '/account' },
  ];
  
  const renderDrawer = () => (
    <Box
      sx={{
        width: isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
        flexShrink: 0,
        transition: theme.transitions.create(['width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
          width: isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
        },
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: [1],
          ...theme.mixins.toolbar,
        }}
      >
        <Box display="flex" alignItems="center" sx={{ flex: 1 }}>
          <DashboardIcon sx={{ color: theme.palette.primary.main, mr: isCollapsed ? 0 : 1 }} />
          <Typography 
            variant="h6" 
            color="primary" 
            sx={{ 
              fontWeight: 'bold',
              transition: theme.transitions.create(['opacity', 'width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              opacity: isCollapsed ? 0 : 1,
              width: isCollapsed ? 0 : 'auto',
              overflow: 'hidden',
              whiteSpace: 'nowrap'
            }}
          >
            {isCollapsed ? 'RS' : 'ReasonShield Vendor Portal'}
          </Typography>
        </Box>
        <Tooltip title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
          <IconButton 
            onClick={toggleDrawer} 
            sx={{ 
              ml: isCollapsed ? 'auto' : 0,
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.light + '20',
              }
            }}
          >
            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Toolbar>
      <Divider />
      <List component="nav" sx={{ px: 1 }}>
        {navigationItems.map((item, index) => (
          item.divider ? (
            <Divider key={`divider-${index}`} sx={{ my: 1 }} />
          ) : (
            <Tooltip 
              key={item.text} 
              title={isCollapsed ? item.text || '' : ''} 
              placement="right"
            >
              <ListItemButton
                component={RouterLink}
                to={item.path || ''}
                selected={isActive(item.path || '')}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  minHeight: 48,
                  justifyContent: isCollapsed ? 'center' : 'initial',
                  px: 2.5,
                  '&.Mui-selected': {
                    backgroundColor: `${theme.palette.primary.light}20`,
                    '&:hover': {
                      backgroundColor: `${theme.palette.primary.light}30`,
                    },
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 0,
                    mr: isCollapsed ? 0 : 3,
                    justifyContent: 'center',
                    color: isActive(item.path || '') ? theme.palette.primary.main : 'inherit'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{ 
                      fontWeight: isActive(item.path || '') ? 'bold' : 'normal',
                      color: isActive(item.path || '') ? theme.palette.primary.main : 'inherit'
                    }} 
                  />
                )}
              </ListItemButton>
            </Tooltip>
          )
        ))}
      </List>
      <Divider />
      {!isCollapsed && (
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Logged in as
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: theme.palette.secondary.main }}>
              <Person fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {authService.getCurrentUser()?.name || authService.getCurrentUser()?.email?.split('@')[0] || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {authService.getCurrentUser()?.email || ''}
              </Typography>
            </Box>
          </Box>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<Logout fontSize="small" />}
            onClick={handleLogout}
            sx={{ mt: 2 }}
          >
            Logout
          </Button>
        </Box>
      )}
    </Box>
  );
  
  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Floating Menu Button for Mobile */}
      {isMobile && !drawerOpen && (
        <Fab
          color="primary"
          aria-label="menu"
          onClick={toggleDrawer}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: theme.zIndex.drawer + 2,
          }}
        >
          <MenuIcon />
        </Fab>
      )}

      {/* Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={toggleDrawer}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              backgroundColor: theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {renderDrawer()}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          open={drawerOpen}
          sx={{
            width: isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
              boxSizing: 'border-box',
              backgroundColor: theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
              overflowX: 'hidden',
              transition: theme.transitions.create(['width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {renderDrawer()}
        </Drawer>
      )}

      {/* Toggle Button for Desktop */}
      {!isMobile && (
        <Fab
          size="small"
          color="primary"
          onClick={toggleDrawer}
          sx={{
            position: 'fixed',
            left: isCollapsed ? COLLAPSED_DRAWER_WIDTH - 20 : DRAWER_WIDTH - 20,
            top: 20,
            zIndex: theme.zIndex.drawer + 2,
            transition: theme.transitions.create(['left'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </Fab>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
          bgcolor: theme.palette.background.default,
          p: 3,
          pt: 3,
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout; 